import { useState, useEffect } from "react";
import ActivityCard from "@/components/ActivityCard";
import PulseWidget from "@/components/PulseWidget";
import DailyPulseCard from "@/components/DailyPulseCard";
import VenueCard from "@/components/VenueCard";
import { MOCK_ACTIVITIES, MOCK_VENUES, ACTIVITY_TYPES, COMMUNITIES, CURRENT_USER, getActivityType } from "@/lib/mockData";
import { apiInvites, apiVenues } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Sparkles } from "lucide-react";

const TABS = ["For you", "Live now", "Today", "Deals", "Nearby"];

const Home = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("For you");
  const [filter, setFilter] = useState(null);
  const [community, setCommunity] = useState(null);
  const [remoteActivities, setRemoteActivities] = useState(null);
  const [remoteVenues, setRemoteVenues] = useState(null);

  useEffect(() => {
    apiInvites.list().then((d) => Array.isArray(d) && setRemoteActivities(d)).catch(() => {});
    apiVenues.list().then((d) => Array.isArray(d) && d.length && setRemoteVenues(d)).catch(() => {});
  }, [user?.user_id]);

  // Prefer real data, fall back to mock so layout never looks empty.
  const sourceActivities = remoteActivities && remoteActivities.length ? remoteActivities : MOCK_ACTIVITIES;
  const sourceVenues = remoteVenues && remoteVenues.length ? remoteVenues : MOCK_VENUES;
  const me = user || CURRENT_USER;

  const sorted = [...sourceActivities].sort((a, b) => (b.match_score || 0) - (a.match_score || 0));

  let activities = sorted;
  if (tab === "Live now") activities = sorted.filter((a) => new Date(a.time_iso).getTime() - Date.now() < 90 * 60 * 1000);
  if (tab === "Today") {
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    activities = sorted.filter((a) => new Date(a.time_iso) <= endOfDay);
  }
  if (tab === "Deals") activities = sorted.filter((a) => a.deal != null);
  if (filter) activities = activities.filter((a) => a.type === filter);
  if (community) activities = activities.filter((a) => a.community === community);

  // Personalised picks — activities matching the user's top types from history
  const topTypes = (me.history && me.history.top_types) || CURRENT_USER.history.top_types;
  const personalised = sorted.filter((a) => topTypes.includes(a.type)).slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* Daily pulse */}
      <div className="mt-4">
        <DailyPulseCard />
      </div>

      {/* Personalised */}
      <div className="brutal-card p-5 mt-5 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FF6A2C]" />
            <span className="label-mono">Based on what you do</span>
          </div>
          <span className="text-xs text-[#555555] font-mono">{(me.history && me.history.joined_count) || CURRENT_USER.history.joined_count} activities</span>
        </div>
        <p className="text-sm text-[#555555] mb-3">
          You usually grabb <span className="font-bold text-[#0A0A0A]">{topTypes.map((t) => getActivityType(t).label.toLowerCase()).join(", ")}</span> around <span className="font-bold text-[#0A0A0A]">{(me.history && me.history.favourite_suburbs && me.history.favourite_suburbs[0]) || CURRENT_USER.history.favourite_suburbs[0]}</span>. Here&apos;s today&apos;s shortlist.
        </p>
        <div className="flex flex-wrap gap-2">
          {personalised.map((a) => (
            <a key={a.id} href={`/activity/${a.id}`} className="brutal-border bg-[#FFFBEB] rounded-full px-3 py-1.5 text-xs font-bold" data-testid={`personalised-${a.id}`}>
              {getActivityType(a.type).emoji} {a.title.length > 28 ? a.title.slice(0, 28) + "…" : a.title}
            </a>
          ))}
        </div>
      </div>

      {/* Communities row */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="label-mono">Find your people</h3>
          {community && (
            <button onClick={() => setCommunity(null)} className="text-xs text-[#555555] underline" data-testid="clear-community">Clear</button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {COMMUNITIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCommunity(community === c.id ? null : c.id)}
              className={`shrink-0 brutal-border rounded-2xl p-3 w-24 text-left ${community === c.id ? "ring-4 ring-[#FF6A2C]/40" : ""}`}
              style={{ background: c.color }}
              data-testid={`community-${c.id}`}
            >
              <div className="text-xl mb-0.5">{c.emoji}</div>
              <div className="font-heading font-bold text-sm leading-tight">{c.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mt-5 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`brutal-border rounded-full px-4 py-2 text-sm font-bold whitespace-nowrap ${
              tab === t ? "bg-[#0A0A0A] text-white brutal-shadow-sm" : "bg-white"
            }`}
            data-testid={`feed-tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Activity type chips */}
      <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar pb-1">
        <button
          onClick={() => setFilter(null)}
          className={`brutal-border rounded-full px-3 py-1.5 text-xs font-bold whitespace-nowrap ${
            !filter ? "bg-[#FFCC00]" : "bg-white"
          }`}
          data-testid="filter-all"
        >
          All
        </button>
        {ACTIVITY_TYPES.slice(0, 10).map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            className={`brutal-border rounded-full px-3 py-1.5 text-xs font-bold whitespace-nowrap ${
              filter === t.id ? "bg-[#FFCC00]" : "bg-white"
            }`}
            data-testid={`filter-${t.id}`}
          >
            {t.emoji} {t.label}
          </button>
        ))}
      </div>

      {/* Deals strip — only on Deals tab */}
      {tab === "Deals" && (
        <div className="mt-5" data-testid="deals-strip">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-black text-2xl">Deals on now</h2>
            <span className="label-mono text-[#555555]">{sourceVenues.filter((v) => v.deal).length} venues</span>
          </div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
            {sourceVenues.filter((v) => v.deal).map((v) => (
              <a
                key={v.id}
                href={`/venues/${v.id}`}
                className="shrink-0 w-64 brutal-card overflow-hidden"
                data-testid={`deal-card-${v.id}`}
              >
                <div className="relative h-28 border-b-2 border-[#0A0A0A]">
                  <img src={v.image} alt={v.name} className="w-full h-full object-cover" />
                  <div
                    className="absolute bottom-2 right-2 brutal-border px-2 py-0.5 rounded-md font-bold text-[10px] uppercase rotate-[-2deg]"
                    style={{ background: v.deal.color, boxShadow: "2px 2px 0px 0px #0A0A0A" }}
                  >
                    {v.deal.label}
                  </div>
                </div>
                <div className="p-3">
                  <div className="font-heading font-bold text-sm leading-tight truncate">{v.name}</div>
                  <div className="text-[10px] text-[#555555] mt-0.5 truncate">{v.category} · {v.distance_km} km</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Pulse widget */}
      <div className="mt-5">
        <PulseWidget />
      </div>

      {/* Activities */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-black text-2xl">Activities</h2>
          <span className="label-mono text-[#555555]">{activities.length} ranked</span>
        </div>
        <div className="space-y-5">
          {activities.map((a, i) => (
            <ActivityCard key={a.id} activity={a} index={i} />
          ))}
          {activities.length === 0 && (
            <div className="brutal-card p-8 text-center">
              <p className="font-bold mb-1">Nothing here yet.</p>
              <p className="text-sm text-[#555555]">Be the first — create an activity.</p>
            </div>
          )}
        </div>
      </div>

      {/* Venues */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-black text-2xl">Nearby venues</h2>
          <span className="label-mono text-[#555555]">Deals on</span>
        </div>
        <div className="space-y-4">
          {sourceVenues.map((v, i) => (
            <VenueCard key={v.id} venue={v} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Home;
